import {
  ArrowRight,
  FileText,
  Link2,
  Mail,
  MessageSquareText,
  PanelLeft,
  Sparkles,
} from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { getClientPortal, getClientVaultContext, getDashboardStats } from "../../lib/api";
import { formatBytes, formatDate } from "../../lib/format";
import type { ClientPortalActivityRecord, ClientPortalPayload, ClientVaultContext, DashboardStats } from "../../lib/types";
import { resolveBusinessCenterBrandingProfile } from "../forms/businessCenterBrandingData";
import {
  buildClientPortalNavigation,
  buildClientPortalScopeSnapshot,
  buildClientPortalWorkspaceDefinition,
  filterByCompanyId,
  buildPortalRecordScopeCards,
  buildPortalScopeExample,
  resolveClientPortalWorkspaceKey,
  portalAccentToClass,
  portalSectionHeadingToClass,
  portalSectionMutedToClass,
  portalSectionToneToClass,
  formatPortalRecordCount,
} from "../../lib/clientPortalFoundation";

type ActivityFilter = "All" | "Messages" | "Files" | "Forms" | "Proposals" | "Agreements" | "Payments" | "Projects";

const activityFilters: ActivityFilter[] = ["All", "Messages", "Files", "Forms", "Proposals", "Agreements", "Payments", "Projects"];

export function UniversalClientPortalPage() {
  const [portal, setPortal] = useState<ClientPortalPayload | null>(null);
  const [vaultContext, setVaultContext] = useState<ClientVaultContext | null>(null);
  const [vaultStats, setVaultStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activityFilter, setActivityFilter] = useState<ActivityFilter>("All");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    void getClientPortal()
      .then(async (data) => {
        if (cancelled) return;
        setPortal(data);
        if (resolveClientPortalWorkspaceKey(data) === "kreatethedate") {
          const [context, stats] = await Promise.all([getClientVaultContext(data.client.id), getDashboardStats()]);
          if (!cancelled) {
            setVaultContext(context);
            setVaultStats(stats);
          }
        } else {
          setVaultContext(null);
          setVaultStats(null);
        }
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError instanceof Error ? loadError.message : "Unable to load the client portal.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!portal) return;
    const workspaceKey = resolveClientPortalWorkspaceKey(portal);
    const brandingProfile = resolveBusinessCenterBrandingProfile(
      (portal.client as { companyId?: string | null }).companyId || null,
      portal.client.clientSlug,
      portal.client.clientName,
      portal.brand,
    );
    const title = `${brandingProfile?.publicDisplayName || buildClientPortalWorkspaceDefinition(workspaceKey).label} Client Portal | ${portal.client.clientName}`;
    document.title = title;
    document.querySelector('meta[name="description"]')?.setAttribute(
      "content",
      brandingProfile?.companyDescription || buildClientPortalWorkspaceDefinition(workspaceKey).description,
    );
  }, [portal]);

  const workspaceKey = useMemo(() => (portal ? resolveClientPortalWorkspaceKey(portal) : null), [portal]);
  const workspace = useMemo(() => (workspaceKey ? buildClientPortalWorkspaceDefinition(workspaceKey) : null), [workspaceKey]);
  const brandingProfile = useMemo(
    () =>
      portal
        ? resolveBusinessCenterBrandingProfile(
            (portal.client as { companyId?: string | null }).companyId || null,
            portal.client.clientSlug,
            portal.client.clientName,
            portal.brand,
          )
        : null,
    [portal],
  );
  const themeTone = workspace?.theme.tone || "light";
  const isTruevance = themeTone === "dark";
  const isTruevanceWorkspace = workspaceKey === "truevance_connect";
  const isKreatethedate = workspaceKey === "kreatethedate";
  const isArRecovery = workspaceKey === "ar_recovery";
  const brandName = brandingProfile?.publicDisplayName || workspace?.label || "Client Portal";
  const clientName = portal?.client.clientName || "Client";
  const portalScope = useMemo(() => (portal ? buildClientPortalScopeSnapshot(portal) : null), [portal]);
  const portalNavigation = useMemo(() => (portalScope && workspaceKey ? buildClientPortalNavigation(portalScope, workspaceKey) : []), [portalScope, workspaceKey]);
  const portalScopeCards = useMemo(
    () => (portal && portalScope && workspaceKey ? buildPortalRecordScopeCards(portal, portalScope, workspaceKey) : []),
    [portal, portalScope, workspaceKey],
  );
  const portalScopeExample = useMemo(() => (portal && portalScope ? buildPortalScopeExample(portal, portalScope) : null), [portal, portalScope]);
  const shellClass = workspace?.theme.shellClass || "min-h-screen bg-white text-[#332a1f]";
  const heroClass = workspace?.theme.heroClass || "border-[#FF6A2B]/20 bg-white text-[#332a1f]";
  const cardClass = workspace?.theme.cardClass || "border-[#FF6A2B]/18 bg-white text-[#332a1f]";
  const mutedClass = workspace?.theme.mutedClass || "text-[#5c5246]";
  const accentClass = workspace?.theme.accentClass || "text-[#D94A16]";

  const filteredActivity = useMemo(() => {
    if (!portalScope) return [];
    if (activityFilter === "All") return portalScope.visibleActivity;
    return portalScope.visibleActivity.filter((item) => classifyFilter(item) === activityFilter);
  }, [activityFilter, portalScope]);

  if (loading) {
    return (
      <main className={shellClass}>
        <section className="px-4 py-10 sm:px-6 lg:px-8">
          <div className={`mx-auto grid max-w-7xl gap-6 rounded-[2rem] border p-6 sm:p-8 ${heroClass}`}>
            <div className="grid gap-3">
              <div className={`h-3 w-28 rounded-full ${isTruevance ? "bg-[rgba(34,224,208,.18)]" : "bg-[#FF6A2B]"}`} />
              <div className={`h-12 w-3/4 rounded-2xl ${isTruevance ? "bg-[rgba(34,224,208,.12)]" : "bg-[#FF6A2B]/35"}`} />
              <div className={`h-5 w-1/2 rounded-2xl ${isTruevance ? "bg-[rgba(34,224,208,.08)]" : "bg-[#FF6A2B]/25"}`} />
            </div>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className={`rounded-2xl border p-4 ${cardClass}`}>
                  <div className={`h-3 w-20 rounded-full ${isTruevance ? "bg-[rgba(34,224,208,.18)]" : "bg-[#FF6A2B]/35"}`} />
                  <div className={`mt-4 h-8 w-24 rounded-full ${isTruevance ? "bg-[rgba(34,224,208,.12)]" : "bg-[#FF6A2B]/25"}`} />
                  <div className={`mt-4 h-3 w-full rounded-full ${isTruevance ? "bg-[rgba(34,224,208,.08)]" : "bg-[#FF6A2B]/20"}`} />
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
    );
  }

  if (error) {
    return (
      <main className={shellClass}>
        <section className="px-4 py-10 sm:px-6 lg:px-8">
          <div className={`mx-auto max-w-5xl rounded-[2rem] border p-6 sm:p-8 ${heroClass}`}>
            <p className={`text-xs font-black uppercase tracking-[0.24em] ${accentClass}`}>{brandName}</p>
            <h1 className="mt-3 font-display text-4xl font-bold">Client Portal</h1>
            <p className={`mt-3 text-sm font-semibold leading-7 ${mutedClass}`}>{error}</p>
          </div>
        </section>
      </main>
    );
  }

  if (!portal) return null;

  const proposalCards = portalScope ? filterByCompanyId(portal.proposals, portalScope.companyId) : portal.proposals;
  const agreementCards = portalScope ? filterByCompanyId(portal.agreements, portalScope.companyId) : portal.agreements;
  const paymentCards = portalScope ? portalScope.visiblePayments : portal.payments;
  const projectCards = portalScope ? portalScope.visibleProjects : portal.projects;
  const overviewCards = [
    { label: "Proposals", value: portal.overview.proposalCount, note: "Proposal history and current review state." },
    { label: "Agreements", value: portal.overview.agreementCount, note: "Signature and completion progress." },
    { label: "Payments", value: portal.overview.paymentCount, note: "Invoice and payment tracking." },
    { label: "Projects", value: portal.overview.projectCount, note: "Delivery and support records." },
  ];

  const primaryAction = workspace?.primaryAction;
  const secondaryActions = workspace?.secondaryActions || [];

  return (
    <main className={shellClass}>
      <section className="px-4 pt-5 sm:px-6 lg:px-8">
        <div className={`mx-auto flex max-w-7xl flex-col gap-4 rounded-[1.75rem] border p-4 sm:p-5 ${heroClass}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className={`text-xs font-black uppercase tracking-[0.24em] ${accentClass}`}>Universal Client Portal</p>
              <h2 className={`mt-2 font-display text-3xl font-bold ${portalSectionHeadingToClass(themeTone)}`}>{workspace?.eyebrow || "Client-facing shell"}</h2>
              <p className={`mt-2 text-sm font-semibold leading-7 ${mutedClass}`}>{workspace?.description || "Separate from the internal admin workspace, with company-scoped record views only."}</p>
            </div>
            {portalScope ? (
              <div className={`rounded-2xl border p-4 ${portalSectionToneToClass(themeTone)}`}>
                <p className={`text-xs font-black uppercase tracking-[0.18em] ${accentClass}`}>Company scope</p>
                <p className={`mt-2 font-display text-2xl font-bold ${portalSectionHeadingToClass(themeTone)}`}>{clientName}</p>
                <p className={`mt-1 text-sm font-semibold ${mutedClass}`}>Visible records are filtered before rendering.</p>
              </div>
            ) : null}
          </div>

          <nav className="flex flex-wrap gap-2" aria-label="Client portal navigation">
            {portalNavigation.map((item) => (
              <a
                key={item.key}
                href={item.href}
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-bold transition ${
                  item.available
                    ? isTruevance
                      ? "border-[rgba(34,224,208,.24)] bg-[rgba(34,224,208,.08)] text-[#F5F7FA] hover:border-[rgba(34,224,208,.42)]"
                      : "border-[#FF6A2B]/25 bg-[#fffdf8] text-[#332a1f] hover:border-[#FF6A2B]/45"
                    : isTruevance
                      ? "border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.03)] text-[#94A3B8]"
                      : "border-[#FF6A2B]/14 bg-white text-[#9a8b74]"
                }`}
              >
                {item.label}
                {!item.available ? <span className="text-[10px] uppercase tracking-[0.16em]">Preview</span> : null}
              </a>
            ))}
          </nav>

          {portalScopeExample ? (
            <div className="grid gap-3 md:grid-cols-4">
              {portalScopeCards.map((card) => (
                <div key={card.label} className={`rounded-2xl border p-4 ${portalSectionToneToClass(themeTone)}`}>
                  <p className={`text-xs font-black uppercase tracking-[0.16em] ${portalAccentToClass(themeTone)}`}>{card.label}</p>
                  <p className={`mt-2 font-display text-3xl font-bold ${portalSectionHeadingToClass(themeTone)}`}>{formatPortalRecordCount(card.value)}</p>
                  <p className={`mt-2 text-sm font-semibold leading-6 ${portalSectionMutedToClass(themeTone)}`}>{card.note}</p>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </section>

      <section id="dashboard" className="px-4 py-8 sm:px-6 lg:px-8">
        <div className={`mx-auto grid max-w-7xl gap-6 rounded-[2rem] border p-6 shadow-[0_24px_80px_rgba(15,23,42,.12)] sm:p-8 ${heroClass}`}>
          <div className="grid gap-4 lg:grid-cols-[1.3fr_.7fr] lg:items-end">
            <div className="grid gap-3">
              <div className={`inline-flex w-fit items-center gap-2 rounded-full border px-3 py-2 text-xs font-black uppercase tracking-[0.22em] ${isTruevance ? "border-[rgba(34,224,208,.3)] bg-[rgba(7,26,47,.52)] text-[#22E0D0]" : "border-[#FF6A2B]/24 bg-[#fff9ef] text-[#D94A16]"}`}>
                <Sparkles className="size-4" aria-hidden="true" />
                {brandName}
              </div>
              <h1 className="max-w-4xl font-display text-5xl font-bold leading-tight sm:text-6xl">{workspace?.title || `${clientName} Portal`}</h1>
              <p className={`max-w-3xl text-base font-semibold leading-8 ${mutedClass}`}>{workspace?.lead || "A shared client experience for proposals, agreements, payments, files, forms, messages, and project history."}</p>
              {brandingProfile ? (
                <p className={`max-w-3xl text-sm font-semibold leading-7 ${mutedClass}`}>
                  Brand profile active: {brandingProfile.portal.subtitle} {brandingProfile.toggles.showPoweredByBusinessCenterOS ? "• Powered by Business Center OS shown" : "• Powered by Business Center OS hidden"}
                </p>
              ) : null}
              <div className="flex flex-wrap gap-3">
                <Link to={primaryAction?.href || "#overview"} className={primaryButtonClass(isTruevance)}>
                  <PanelLeft className="size-4" aria-hidden="true" />
                  {primaryAction?.label || "Open Client Overview"}
                </Link>
                {secondaryActions.map((action) =>
                  action.href.startsWith("#") ? (
                    <a key={action.label} href={action.href} className={secondaryButtonClass(isTruevance)}>
                      <ArrowRight className="size-4" aria-hidden="true" />
                      {action.label}
                    </a>
                  ) : (
                    <Link key={action.label} to={action.href} className={secondaryButtonClass(isTruevance)}>
                      <ArrowRight className="size-4" aria-hidden="true" />
                      {action.label}
                    </Link>
                  ),
                )}
              </div>
            </div>

            <div className={`grid gap-4 rounded-3xl border p-5 ${isTruevance ? "border-[rgba(34,224,208,.16)] bg-[rgba(255,255,255,.04)]" : "border-[#FF6A2B]/18 bg-[#fffdf8]"}`}>
              <p className={`text-xs font-black uppercase tracking-[0.16em] ${accentClass}`}>Portal status</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <MetricTile label="Open items" value={portal.overview.openItems} note="Outstanding tasks and milestones." tone={themeTone} />
                <MetricTile label="Recent activity" value={portal.overview.activityCount} note="Messages, records, and workflow history." tone={themeTone} />
              </div>
              <p className={`text-sm font-semibold leading-7 ${mutedClass}`}>{portal.overview.nextStep}</p>
            </div>
          </div>
        </div>
      </section>

      <section id="overview" className="px-4 pb-6 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-4 md:grid-cols-2 xl:grid-cols-4">
          {overviewCards.map((metric) => (
            <article key={metric.label} className={`rounded-2xl border p-5 ${cardClass}`}>
              <p className={`text-xs font-black uppercase tracking-[0.18em] ${accentClass}`}>{metric.label}</p>
              <p className={`mt-2 font-display text-4xl font-bold ${isTruevance ? "text-[#F5F7FA]" : "text-[#071A2F]"}`}>{metric.value}</p>
              <p className={`mt-3 text-sm font-semibold leading-6 ${mutedClass}`}>{metric.note}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="tasks" className="px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-6 xl:grid-cols-[1.02fr_.98fr]">
          <PortalSectionCard
            title={workspace?.tasksSection.title || "Tasks"}
            eyebrow={workspace?.tasksSection.eyebrow || "Assigned work"}
            description={workspace?.tasksSection.description || "Company-scoped work items for the client portal foundation."}
            tone={themeTone}
          >
            <div className="grid gap-3">
              {portalScope?.visibleTasks.length ? (
                portalScope.visibleTasks.map((task) => (
                  <article key={task.id} className={`rounded-2xl border p-4 ${cardClass}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className={`text-xs font-black uppercase tracking-[0.18em] ${accentClass}`}>{task.status}</p>
                        <h3 className={`mt-2 font-display text-2xl font-bold ${isTruevance ? "text-[#F5F7FA]" : "text-[#071A2F]"}`}>{task.taskTitle}</h3>
                      </div>
                      <span className={`rounded-full px-3 py-1 text-xs font-black uppercase tracking-[0.12em] ${isTruevance ? "bg-[rgba(34,224,208,.14)] text-[#22E0D0]" : "bg-[#FF6A2B]/18 text-[#D94A16]"}`}>
                        {task.priority}
                      </span>
                    </div>
                    <p className={`mt-3 text-sm font-semibold leading-6 ${mutedClass}`}>{task.description}</p>
                    <p className={`mt-3 text-sm font-semibold ${mutedClass}`}>
                      {task.clientName} {task.projectName ? `• ${task.projectName}` : ""}
                    </p>
                  </article>
                ))
              ) : (
                <EmptyPortalState tone={themeTone} title={workspace?.tasksSection.emptyTitle || "No tasks visible yet"} message={workspace?.tasksSection.emptyMessage || "Tasks assigned to this company will appear here once portal task loading is connected."} />
              )}
            </div>
          </PortalSectionCard>

          <PortalSectionCard
            title={workspace?.documentsSection.title || "Documents"}
            eyebrow={workspace?.documentsSection.eyebrow || "Scoped files"}
            description={workspace?.documentsSection.description || "Client and project documents limited to the current company."}
            tone={themeTone}
          >
            <div className="grid gap-3">
              {portalScope?.visibleDocuments.length ? (
                portalScope.visibleDocuments.map((document) => (
                  <article key={document.id} className={`rounded-2xl border p-4 ${cardClass}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className={`text-xs font-black uppercase tracking-[0.18em] ${accentClass}`}>{document.documentType}</p>
                        <h3 className={`mt-2 font-display text-2xl font-bold ${isTruevance ? "text-[#F5F7FA]" : "text-[#071A2F]"}`}>{document.documentName}</h3>
                      </div>
                      <span className={`rounded-full px-3 py-1 text-xs font-black uppercase tracking-[0.12em] ${isTruevance ? "bg-[rgba(34,224,208,.14)] text-[#22E0D0]" : "bg-[#FF6A2B]/18 text-[#D94A16]"}`}>
                        {document.status}
                      </span>
                    </div>
                    <p className={`mt-3 text-sm font-semibold leading-6 ${mutedClass}`}>{document.notes || "No notes added yet."}</p>
                    <p className={`mt-3 text-sm font-semibold ${mutedClass}`}>
                      {document.clientName} {document.projectName ? `• ${document.projectName}` : ""}
                    </p>
                  </article>
                ))
              ) : (
                <EmptyPortalState tone={themeTone} title={workspace?.documentsSection.emptyTitle || "No documents visible yet"} message={workspace?.documentsSection.emptyMessage || "Document loading will respect company scope once record wiring is connected."} />
              )}
            </div>
          </PortalSectionCard>
        </div>
      </section>

      <section id="reports" className="px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <PortalSectionCard
            title={isKreatethedate ? "Archive Reports" : isTruevanceWorkspace ? "Reports" : isArRecovery ? "Recovery Reports" : "Reports"}
            eyebrow={isKreatethedate ? "Read-only summaries" : isTruevanceWorkspace ? "Read-only summaries" : isArRecovery ? "Read-only summaries" : "Read-only summaries"}
            description={isKreatethedate ? "Portal report surfaces are foundation only and will stay company-scoped." : isTruevanceWorkspace ? "Portal report surfaces are foundation only and will stay company-scoped." : isArRecovery ? "Portal report surfaces are foundation only and will stay company-scoped." : "Portal report surfaces are foundation only and will stay company-scoped."}
            tone={themeTone}
          >
            <div className="grid gap-3 md:grid-cols-3">
              <PortalMiniCard label="Project scope" value={formatPortalRecordCount(portalScope?.visibleProjects.length || 0)} note="Visible project records only." tone={themeTone} />
              <PortalMiniCard label="Task scope" value={formatPortalRecordCount(portalScope?.visibleTasks.length || 0)} note="Assigned work records only." tone={themeTone} />
              <PortalMiniCard label="Document scope" value={formatPortalRecordCount(portalScope?.visibleDocuments.length || 0)} note="Company document records only." tone={themeTone} />
            </div>
          </PortalSectionCard>
        </div>
      </section>

      {isTruevanceWorkspace ? (
        <section id="assessments" className="px-4 py-6 sm:px-6 lg:px-8">
          <PortalSectionCard
            title="Assessments, Recommendations, and Roadmaps"
            eyebrow="Consulting planning"
            description="A shared view of the consulting workstream before and after proposal approval."
            tone="dark"
          >
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <PortalMiniCard label="Assessments" value={Math.max(portal.overview.projectCount, portal.overview.activityCount)} note="Discovery and diagnostic work in progress." tone="dark" />
              <PortalMiniCard label="Recommendations" value={portal.overview.proposalCount} note="Solution direction and scope refinement." tone="dark" />
              <PortalMiniCard label="Roadmaps" value={projectCards.length || portal.overview.projectCount} note="Implementation planning and phased delivery." tone="dark" />
              <PortalMiniCard label="Support Workflow" value={portal.overview.openItems} note="Open follow-ups, reminders, and client support." tone="dark" />
            </div>
          </PortalSectionCard>
        </section>
      ) : null}

      <section id="proposals" className="px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-6 xl:grid-cols-[1.02fr_.98fr]">
          <PortalSectionCard
            title={isKreatethedate ? "Agreements" : isTruevanceWorkspace ? "Recommendations" : isArRecovery ? "Statements" : "Statements"}
            eyebrow={isKreatethedate ? "Service selection and acceptance" : isTruevanceWorkspace ? "Consulting planning" : isArRecovery ? "Recovery statement review" : "Recovery statement review"}
            description={isKreatethedate ? "Review the latest agreements, selected services, and current approval state." : isTruevanceWorkspace ? "Review the latest recommendations, selected services, and current approval state." : isArRecovery ? "Review the latest statements, selected balances, and current recovery state." : "Review the latest statements, selected balances, and current recovery state."}
            tone={themeTone}
          >
            <div className="grid gap-3">
              {proposalCards.length ? (
                proposalCards.map((proposal) => (
                  <RecordCard
                    key={proposal.id}
                    title={proposal.proposalNumber}
                    subtitle={proposal.packageName}
                    status={proposal.status}
                    detail={`${proposal.selectedServices || "No services recorded"}${proposal.recommendedAddons ? ` • Add-ons: ${proposal.recommendedAddons}` : ""}`}
                    amount={formatMoney(proposal.totalCents)}
                    secondary={formatMoney(proposal.balanceDueCents)}
                    link={proposal.reviewUrl || undefined}
                    linkLabel="Review proposal"
                    tone={themeTone}
                  />
                ))
              ) : (
                <EmptyPortalState tone={themeTone} title="No records yet" message="Your records will appear here when they are created and shared." />
              )}
            </div>
          </PortalSectionCard>

          <PortalSectionCard
            title={isKreatethedate ? "Agreements and Invoices" : isTruevanceWorkspace ? "Documents and Invoices" : isArRecovery ? "Collection Letters and Invoices" : "Collection Letters and Invoices"}
            eyebrow={isKreatethedate ? "Approvals and billing" : isTruevanceWorkspace ? "Client files and billing" : isArRecovery ? "Recovery correspondence and billing" : "Recovery correspondence and billing"}
            description={isKreatethedate ? "Track what has been signed, what has been invoiced, and what still needs attention." : isTruevanceWorkspace ? "Track what has been signed, what has been invoiced, and what still needs attention." : isArRecovery ? "Track what has been sent, what has been invoiced, and what still needs attention." : "Track what has been sent, what has been invoiced, and what still needs attention."}
            tone={themeTone}
          >
            <div className="grid gap-3">
              {agreementCards.length ? (
                agreementCards.map((agreement) => (
                  <RecordCard
                    key={agreement.id}
                    title={agreement.agreementNumber}
                    subtitle={agreement.proposalNumber}
                    status={agreement.status}
                    detail={`${agreement.provider}${agreement.signedAt ? ` • Signed ${formatDate(agreement.signedAt)}` : ""}`}
                    link={agreement.agreementUrl}
                    linkLabel="Open agreement"
                    tone={themeTone}
                  />
                ))
              ) : (
                <EmptyPortalState tone={themeTone} title="No records yet" message="Agreement and billing records will appear here after approval." />
              )}

              {paymentCards.length ? (
                paymentCards.map((payment) => (
                  <RecordCard
                    key={payment.id}
                    title={payment.invoiceNumber}
                    subtitle={payment.proposalNumber}
                    status={`${payment.status} • ${payment.invoiceStatus}`}
                    detail={`${formatMoney(payment.amountPaidCents)} paid of ${formatMoney(payment.totalCents)}`}
                    amount={formatMoney(payment.amountRemainingCents)}
                    secondary={payment.dueDate ? `Due ${formatDate(payment.dueDate)}` : "No due date"}
                    link={payment.stripeCheckoutUrl || payment.stripeInvoiceUrl || undefined}
                    linkLabel="Open payment"
                    tone={themeTone}
                  />
                ))
              ) : (
                <EmptyPortalState tone={themeTone} title="No invoices yet" message="Invoices and payment history will appear here once billing is created." />
              )}
            </div>
          </PortalSectionCard>
        </div>
      </section>

      <section id="projects" className="px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-6 xl:grid-cols-[1.02fr_.98fr]">
          <PortalSectionCard
            title={isKreatethedate ? "Files and Deliverables" : isTruevanceWorkspace ? "Projects and Deliverables" : isArRecovery ? "Customer Accounts" : "Customer Accounts"}
            eyebrow={isKreatethedate ? "Memory Hub delivery" : isTruevanceWorkspace ? "Consulting delivery" : isArRecovery ? "Recovery accounts" : "Recovery accounts"}
            description={isKreatethedate ? "Keep Memory Hub assets, delivery items, and approved files in one place." : isTruevanceWorkspace ? "Track project milestones, deliverables, and support items from one branded workspace." : isArRecovery ? "Track recovery balances, follow-up items, and customer account milestones from one branded workspace." : "Track recovery balances, follow-up items, and customer account milestones from one branded workspace."}
            tone={themeTone}
          >
            {isKreatethedate ? (
              <div className="grid gap-3">
                <div className={`rounded-2xl border p-4 ${cardClass}`}>
                  <p className={`text-xs font-black uppercase tracking-[0.18em] ${accentClass}`}>Memory Media Vault</p>
                  <p className={`mt-2 text-sm font-semibold leading-6 ${mutedClass}`}>
                    {vaultStats
                      ? `${vaultStats.pendingUploads} pending uploads • ${vaultStats.approvedMedia} approved media • ${formatBytes(vaultStats.storageBytes)} stored`
                      : "Upload and approval activity will appear here."}
                  </p>
                </div>
                {(vaultContext?.vaults || []).map((vault) => (
                  <div key={vault.id} className={`rounded-2xl border p-4 ${cardClass}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className={`text-xs font-black uppercase tracking-[0.18em] ${accentClass}`}>{vault.event.eventName}</p>
                        <h3 className={`mt-2 font-display text-2xl font-bold ${isTruevance ? "text-[#F5F7FA]" : "text-[#071A2F]"}`}>{vault.vaultName}</h3>
                      </div>
                      <span className={`rounded-full px-3 py-1 text-xs font-black uppercase tracking-[0.12em] ${isTruevance ? "bg-[rgba(34,224,208,.14)] text-[#22E0D0]" : "bg-[#FF6A2B]/18 text-[#D94A16]"}`}>
                        {vault.event.uploadStatus}
                      </span>
                    </div>
                    <p className={`mt-3 text-sm font-semibold leading-6 ${mutedClass}`}>
                      Gallery: {vault.event.galleryUrl}
                      <br />
                      Memory Hub: {vault.event.memoryHubUrl}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid gap-3">
                {projectCards.length ? (
                  projectCards.map((project) => (
                    <div key={project.id} className={`rounded-2xl border p-4 ${cardClass}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className={`text-xs font-black uppercase tracking-[0.18em] ${accentClass}`}>{project.projectPhase}</p>
                          <h3 className={`mt-2 font-display text-2xl font-bold ${isTruevance ? "text-[#F5F7FA]" : "text-[#071A2F]"}`}>{project.projectName}</h3>
                        </div>
                        <span className={`rounded-full px-3 py-1 text-xs font-black uppercase tracking-[0.12em] ${isTruevance ? "bg-[rgba(34,224,208,.14)] text-[#22E0D0]" : "bg-[#FF6A2B]/18 text-[#D94A16]"}`}>
                          {project.projectStatus}
                        </span>
                      </div>
                      <p className={`mt-3 text-sm font-semibold leading-6 ${mutedClass}`}>
                        {project.milestones.length ? `${project.milestones.join(" • ")}.` : "Milestones will appear here as the project advances."}
                      </p>
                      <p className={`mt-2 text-sm font-semibold leading-6 ${mutedClass}`}>
                        {project.deliverables.length ? `Deliverables: ${project.deliverables.join(" • ")}` : "Deliverables will appear here once they are added."}
                      </p>
                    </div>
                  ))
                ) : (
                  <EmptyPortalState tone={themeTone} title="No records yet" message="Workspace records will appear here once the company scope is connected." />
                )}
              </div>
            )}
          </PortalSectionCard>

          <PortalSectionCard
            title={workspace?.messagesSection.title || "Messages and Activity"}
            eyebrow={workspace?.messagesSection.eyebrow || "Timeline and history"}
            description={workspace?.messagesSection.description || "Follow notes, communications, and timeline events without leaving the portal."}
            tone={themeTone}
          >
            <div className="mb-4 flex flex-wrap gap-2">
              {activityFilters.map((filter) => (
                <button
                  key={filter}
                  type="button"
                  onClick={() => setActivityFilter(filter)}
                  className={`rounded-full border px-3 py-2 text-xs font-black uppercase tracking-[0.14em] transition ${
                    activityFilter === filter
                      ? isTruevance
                        ? "border-[rgba(34,224,208,.3)] bg-[rgba(34,224,208,.16)] text-[#F5F7FA]"
                        : "border-[#FF6A2B] bg-[#FF6A2B]/20 text-[#332a1f]"
                      : isTruevance
                        ? "border-[rgba(34,224,208,.16)] bg-[rgba(255,255,255,.04)] text-[#E8EEF4]"
                        : "border-[#FF6A2B]/18 bg-white text-[#5c5246]"
                  }`}
                >
                  {filter}
                </button>
              ))}
            </div>

            <div className="grid gap-3">
              {filteredActivity.length ? (
                filteredActivity.map((item) => (
                  <article key={item.id} className={`rounded-2xl border p-4 ${cardClass}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className={`text-xs font-black uppercase tracking-[0.18em] ${accentClass}`}>{labelActivityKind(item.kind)}</p>
                        <h3 className={`mt-2 font-display text-2xl font-bold ${isTruevance ? "text-[#F5F7FA]" : "text-[#071A2F]"}`}>{item.title}</h3>
                      </div>
                      <span className={`text-xs font-black uppercase tracking-[0.14em] ${isTruevance ? "text-[#22E0D0]" : "text-[#D94A16]"}`}>{formatDate(item.createdAt)}</span>
                    </div>
                    <p className={`mt-3 text-sm font-semibold leading-7 ${mutedClass}`}>{item.detail}</p>
                    {item.relatedUrl ? (
                      <a href={item.relatedUrl} className={`mt-4 inline-flex items-center gap-2 text-sm font-extrabold ${isTruevance ? "text-[#22E0D0]" : "text-[#D94A16]"}`}>
                        Open related item
                        <ArrowRight className="size-4" aria-hidden="true" />
                      </a>
                    ) : null}
                  </article>
                ))
              ) : (
                <EmptyPortalState tone={themeTone} title="No activity yet" message="Activity, messages, and workflow notes will appear here as work progresses." />
              )}
            </div>
          </PortalSectionCard>
        </div>
      </section>

      <section id="billing" className="px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <PortalSectionCard
            title={isKreatethedate ? "Invoices" : isTruevanceWorkspace ? "Invoices" : isArRecovery ? "Statements" : "Statements"}
            eyebrow={isKreatethedate ? "Invoices and payment history" : isTruevanceWorkspace ? "Invoices and payment history" : isArRecovery ? "Statements and payment history" : "Statements and payment history"}
            description={isKreatethedate ? "Billing access remains read-only for the foundation pass." : isTruevanceWorkspace ? "Billing access remains read-only for the foundation pass." : isArRecovery ? "Billing access remains read-only for the foundation pass." : "Billing access remains read-only for the foundation pass."}
            tone={isTruevance ? "dark" : "light"}
          >
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              {(portalScope?.visiblePayments || []).slice(0, 4).map((payment) => (
                <RecordCard
                  key={payment.id}
                  title={payment.invoiceNumber}
                  subtitle={payment.proposalNumber}
                  status={payment.status}
                  detail={`${formatMoney(payment.amountPaidCents)} paid of ${formatMoney(payment.totalCents)}`}
                  secondary={payment.dueDate ? `Due ${formatDate(payment.dueDate)}` : "No due date"}
                  tone={isTruevance ? "dark" : "light"}
                />
              ))}
              {!portalScope?.visiblePayments.length ? (
                <EmptyPortalState tone={isTruevance ? "dark" : "light"} title="No billing records visible yet" message="Invoices and payment history will appear here once scope-linked billing records are available." />
              ) : null}
            </div>
          </PortalSectionCard>
        </div>
      </section>

      <section id="messages" className="px-4 py-6 pb-12 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-[.9fr_1.1fr]">
          <PortalSectionCard
            title={workspace?.messagesSection.title || "Messages and Files"}
            eyebrow={workspace?.messagesSection.eyebrow || "Brand-scoped support"}
            description={workspace?.messagesSection.description || "Support updates are grouped inside the same shared experience."}
            tone={themeTone}
          >
            <div className="grid gap-3">
              <InfoRow icon={Mail} label="Messages" value="Saved in the activity timeline" tone={themeTone} />
              <InfoRow
                icon={Link2}
                label={isKreatethedate ? "Files" : isTruevanceWorkspace ? "Files" : "Letters"}
                value={isKreatethedate ? "Memory Hub uploads and galleries" : isTruevanceWorkspace ? "Project deliverables and records" : "Collection letters and statements"}
                tone={themeTone}
              />
              <InfoRow
                icon={FileText}
                label={isKreatethedate ? "Forms" : isTruevanceWorkspace ? "Forms" : "Reports"}
                value={isKreatethedate ? "RSVP, Memory, Archive, and Vault forms" : isTruevanceWorkspace ? "Assessment, proposal, roadmap, and support records" : "Aging, recovery, and escalation reports"}
                tone={themeTone}
              />
              <InfoRow icon={MessageSquareText} label="Follow-up" value="Client reminders and next steps tracked here" tone={themeTone} />
              <div className={`rounded-2xl border p-4 ${isTruevance ? "border-[rgba(34,224,208,.16)] bg-[rgba(255,255,255,.04)]" : "border-[#FF6A2B]/18 bg-[#fffdf8]"}`}>
                <p className={`text-xs font-black uppercase tracking-[0.18em] ${accentClass}`}>{workspace?.messagesSection.placeholderLabel || "Messages placeholder"}</p>
                <p className={`mt-2 text-sm font-semibold leading-7 ${mutedClass}`}>{workspace?.messagesSection.placeholderMessage || "A future shared inbox will live here after portal messaging is connected."}</p>
              </div>
            </div>
          </PortalSectionCard>

          <PortalSectionCard
            title={workspace?.experienceSection.title || "Brand Experience"}
            eyebrow={workspace?.experienceSection.eyebrow || "Workspace journey"}
            description={workspace?.experienceSection.description || "Workspace-specific experiences remain brand-scoped and client-owned."}
            tone={themeTone}
          >
            <div className={`grid gap-3 ${workspaceKey === "kreatethedate" ? "sm:grid-cols-2 xl:grid-cols-4" : "sm:grid-cols-2 xl:grid-cols-4"}`}>
              {(workspace?.experienceSection.modules || []).map((module) => (
                <PortalLinkCard key={module.label} to={module.href} label={module.label} description={module.description} tone={themeTone} />
              ))}
            </div>
          </PortalSectionCard>
        </div>
      </section>
    </main>
  );
}

function PortalSectionCard({
  title,
  eyebrow,
  description,
  tone,
  children,
}: {
  title: string;
  eyebrow: string;
  description: string;
  tone: "dark" | "light";
  children: ReactNode;
}) {
  const dark = tone === "dark";
  return (
    <article className={`rounded-[2rem] border p-6 sm:p-8 ${dark ? "border-[rgba(34,224,208,.16)] bg-[rgba(7,26,47,.76)]" : "border-[#FF6A2B]/18 bg-white"}`}>
      <p className={`text-xs font-black uppercase tracking-[0.24em] ${dark ? "text-[#22E0D0]" : "text-[#D94A16]"}`}>{eyebrow}</p>
      <h2 className={`mt-2 font-display text-3xl font-bold ${dark ? "text-[#F5F7FA]" : "text-[#071A2F]"}`}>{title}</h2>
      <p className={`mt-3 text-sm font-semibold leading-7 ${dark ? "text-[#E8EEF4]" : "text-[#5c5246]"}`}>{description}</p>
      <div className="mt-6">{children}</div>
    </article>
  );
}

function RecordCard({
  title,
  subtitle,
  status,
  detail,
  amount,
  secondary,
  link,
  linkLabel,
  tone,
}: {
  title: string;
  subtitle: string;
  status: string;
  detail: string;
  amount?: string;
  secondary?: string;
  link?: string | null;
  linkLabel?: string;
  tone: "dark" | "light";
}) {
  const dark = tone === "dark";
  return (
    <article className={`rounded-2xl border p-4 ${dark ? "border-[rgba(34,224,208,.16)] bg-[rgba(255,255,255,.04)]" : "border-[#FF6A2B]/18 bg-[#fffdf8]"}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className={`text-xs font-black uppercase tracking-[0.18em] ${dark ? "text-[#22E0D0]" : "text-[#D94A16]"}`}>{subtitle}</p>
          <h3 className={`mt-2 font-display text-2xl font-bold ${dark ? "text-[#F5F7FA]" : "text-[#071A2F]"}`}>{title}</h3>
        </div>
        <span className={`rounded-full px-3 py-1 text-xs font-black uppercase tracking-[0.14em] ${dark ? "bg-[rgba(34,224,208,.14)] text-[#22E0D0]" : "bg-[#FF6A2B]/18 text-[#D94A16]"}`}>
          {status}
        </span>
      </div>
      <p className={`mt-3 text-sm font-semibold leading-7 ${dark ? "text-[#E8EEF4]" : "text-[#5c5246]"}`}>{detail}</p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        {amount ? <p className={`text-sm font-black ${dark ? "text-[#F5F7FA]" : "text-[#071A2F]"}`}>{amount}</p> : null}
        {secondary ? <p className={`text-sm font-semibold ${dark ? "text-[#E8EEF4]" : "text-[#5c5246]"}`}>{secondary}</p> : null}
        {link ? (
          <a
            href={link}
            target={link.startsWith("http") ? "_blank" : undefined}
            rel={link.startsWith("http") ? "noreferrer" : undefined}
            className={`inline-flex items-center gap-2 text-sm font-extrabold ${dark ? "text-[#22E0D0]" : "text-[#D94A16]"}`}
          >
            {linkLabel || "Open"}
            <ArrowRight className="size-4" aria-hidden="true" />
          </a>
        ) : null}
      </div>
    </article>
  );
}

function EmptyPortalState({
  title,
  message,
  tone,
}: {
  title: string;
  message: string;
  tone: "dark" | "light";
}) {
  const dark = tone === "dark";
  return (
    <div className={`rounded-2xl border p-5 ${dark ? "border-[rgba(34,224,208,.16)] bg-[rgba(255,255,255,.04)]" : "border-[#FF6A2B]/18 bg-[#fffdf8]"}`}>
      <p className={`font-display text-2xl font-bold ${dark ? "text-[#F5F7FA]" : "text-[#071A2F]"}`}>{title}</p>
      <p className={`mt-2 text-sm font-semibold leading-7 ${dark ? "text-[#E8EEF4]" : "text-[#5c5246]"}`}>{message}</p>
    </div>
  );
}

function MetricTile({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string | number;
  note: string;
  tone: "dark" | "light";
}) {
  const dark = tone === "dark";
  return (
    <div className={`rounded-2xl border p-4 ${dark ? "border-[rgba(34,224,208,.16)] bg-[rgba(255,255,255,.04)]" : "border-[#FF6A2B]/18 bg-[#fffdf8]"}`}>
      <p className={`text-xs font-black uppercase tracking-[0.18em] ${dark ? "text-[#22E0D0]" : "text-[#D94A16]"}`}>{label}</p>
      <p className={`mt-2 font-display text-3xl font-bold ${dark ? "text-[#F5F7FA]" : "text-[#071A2F]"}`}>{value}</p>
      <p className={`mt-2 text-sm font-semibold leading-6 ${dark ? "text-[#E8EEF4]" : "text-[#5c5246]"}`}>{note}</p>
    </div>
  );
}

function PortalLinkCard({
  to,
  label,
  description,
  tone,
}: {
  to: string;
  label: string;
  description: string;
  tone: "dark" | "light";
}) {
  const dark = tone === "dark";
  return (
    <Link
      to={to}
      className={`rounded-2xl border p-4 transition hover:-translate-y-0.5 ${
        dark ? "border-[rgba(34,224,208,.16)] bg-[rgba(255,255,255,.04)] hover:border-[rgba(34,224,208,.3)]" : "border-[#FF6A2B]/18 bg-[#fffdf8] hover:border-[#FF6A2B]/35"
      }`}
    >
      <p className={`text-xs font-black uppercase tracking-[0.18em] ${dark ? "text-[#22E0D0]" : "text-[#D94A16]"}`}>Experience</p>
      <h3 className={`mt-2 font-display text-2xl font-bold ${dark ? "text-[#F5F7FA]" : "text-[#071A2F]"}`}>{label}</h3>
      <p className={`mt-2 text-sm font-semibold leading-6 ${dark ? "text-[#E8EEF4]" : "text-[#5c5246]"}`}>{description}</p>
    </Link>
  );
}

function PortalMiniCard({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string | number;
  note: string;
  tone: "dark" | "light";
}) {
  const dark = tone === "dark";
  return (
    <div className={`rounded-2xl border p-4 ${dark ? "border-[rgba(34,224,208,.16)] bg-[rgba(255,255,255,.04)]" : "border-[#FF6A2B]/18 bg-[#fffdf8]"}`}>
      <p className={`text-xs font-black uppercase tracking-[0.18em] ${dark ? "text-[#22E0D0]" : "text-[#D94A16]"}`}>{label}</p>
      <p className={`mt-2 font-display text-3xl font-bold ${dark ? "text-[#F5F7FA]" : "text-[#071A2F]"}`}>{value}</p>
      <p className={`mt-2 text-sm font-semibold leading-6 ${dark ? "text-[#E8EEF4]" : "text-[#5c5246]"}`}>{note}</p>
    </div>
  );
}

function InfoRow({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: typeof Mail;
  label: string;
  value: string;
  tone: "dark" | "light";
}) {
  const dark = tone === "dark";
  return (
    <div className={`flex items-start gap-3 rounded-2xl border p-4 ${dark ? "border-[rgba(34,224,208,.16)] bg-[rgba(255,255,255,.04)]" : "border-[#FF6A2B]/18 bg-[#fffdf8]"}`}>
      <span className={`grid size-10 shrink-0 place-items-center rounded-full ${dark ? "bg-[rgba(34,224,208,.14)] text-[#22E0D0]" : "bg-[#FF6A2B]/18 text-[#D94A16]"}`}>
        <Icon className="size-4" aria-hidden="true" />
      </span>
      <div>
        <p className={`text-xs font-black uppercase tracking-[0.18em] ${dark ? "text-[#22E0D0]" : "text-[#D94A16]"}`}>{label}</p>
        <p className={`mt-1 text-sm font-semibold leading-6 ${dark ? "text-[#F5F7FA]" : "text-[#332a1f]"}`}>{value}</p>
      </div>
    </div>
  );
}

function classifyFilter(item: ClientPortalActivityRecord): ActivityFilter {
  switch (item.kind) {
    case "message":
      return "Messages";
    case "file":
      return "Files";
    case "form":
      return "Forms";
    case "proposal":
      return "Proposals";
    case "agreement":
      return "Agreements";
    case "payment":
      return "Payments";
    case "project":
      return "Projects";
    default:
      return "All";
  }
}

function labelActivityKind(kind: ClientPortalActivityRecord["kind"]) {
  switch (kind) {
    case "message":
      return "Message";
    case "file":
      return "File";
    case "form":
      return "Form";
    case "proposal":
      return "Proposal";
    case "agreement":
      return "Agreement";
    case "payment":
      return "Payment";
    case "project":
      return "Project";
    default:
      return "Timeline";
  }
}

function formatMoney(cents = 0) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format((Number(cents) || 0) / 100);
}

function primaryButtonClass(isTruevance: boolean) {
  return `inline-flex items-center justify-center gap-2 rounded-full px-4 py-3 text-sm font-extrabold transition ${
    isTruevance
      ? "bg-[#E0C96A] text-[#071A2F] hover:bg-[#F0DA85]"
      : "bg-[#FF6A2B] text-[#050505] hover:bg-[#FF6A2B]"
  }`;
}

function secondaryButtonClass(isTruevance: boolean) {
  return `inline-flex items-center justify-center gap-2 rounded-full border px-4 py-3 text-sm font-extrabold transition ${
    isTruevance
      ? "border-[rgba(34,224,208,.24)] bg-[rgba(255,255,255,.04)] text-[#F5F7FA] hover:border-[rgba(34,224,208,.44)] hover:bg-[rgba(34,224,208,.08)]"
      : "border-[#FF6A2B]/35 bg-white text-[#332a1f] hover:border-[#FF6A2B]/55 hover:bg-[#fff9ef]"
  }`;
}
